<template>
  <div v-if="mountError">
    <div class="p-4 text-red-600 font-bold">
      {{ mountError }}
    </div>
  </div>
  <div v-else class="p-4 space-y-8">
    <h1 class="text-2xl font-bold mb-4">SLA Reports — Current Month</h1>

    <!-- SLA Banner -->
    <section>
      <div
        v-if="slaBanner"
        :class="[
          'p-4 mb-6 rounded text-white font-bold text-center',
          slaBanner === 'MET' ? 'bg-green-700' : 'bg-orange-300'
        ]"
        @mouseenter="handleConfetti"
      >
        SLA {{ slaBanner }}
      </div>
    </section>

    <!-- Uptime check -->
    <section>
      <h2 class="text-lg mb-2">Availability (portal) business & non-business hours</h2>
      <div class="relative w-full" style="height:33vh">
        <Line v-if="uptimeData" :data="uptimeData" :options="uptimeOptions" class="w-full" />
        <LoadingState v-else-if="loadingMetrics" class="absolute inset-0 flex items-center justify-center italic"></LoadingState>
        <div v-else class="absolute inset-0 flex items-center justify-center text-red-600">{{ apiError }}</div>
      </div>
    </section>

    <!-- Latency -->
    <section>
      <h2 class="text-lg mb-2">Page load time (eu) business & non-business hours</h2>
      <div class="relative w-full" style="height:33vh">
        <Line v-if="latencyData" :data="latencyData" :options="latencyOptions" class="w-full" />
        <LoadingState v-else-if="loadingMetrics" class="absolute inset-0 flex items-center justify-center italic"></LoadingState>
        <div v-else class="absolute inset-0 flex items-center justify-center text-red-600">{{ apiError }}</div>
      </div>
    </section>

    <!-- SLO Health -->
    <section>
      <h2 class="text-lg mb-2">Availability Celonis Integrations (business & non-business hours)</h2>
      <div class="relative w-full" style="height:33vh">
        <Line v-if="sloHealthData" :data="sloHealthData" :options="sloHealthOptions" class="w-full" />
        <LoadingState v-else-if="loadingMetrics" class="absolute inset-0 flex items-center justify-center italic"></LoadingState>
        <div v-else class="absolute inset-0 flex items-center justify-center text-red-600">{{ apiError }}</div>
      </div>
    </section>

    <!-- SLO Error-budget -->
    <section>
      <h2 class="text-lg mb-2">Availability PAC Error-budget Remaining (current month)</h2>
      <div class="relative w-full" style="height:33vh">
        <Line v-if="sloBudgetData" :data="sloBudgetData" :options="sloBudgetOptions" class="w-full" />
        <LoadingState v-else-if="loadingMetrics" class="absolute inset-0 flex items-center justify-center italic"></LoadingState>
        <div v-else class="absolute inset-0 flex items-center justify-center text-red-600">{{ apiError }}</div>
      </div>
    </section>

    <!-- Execution Time -->
    <section>
      <h2 class="text-lg mb-2">Calculation/Execution time</h2>
      <div class="relative w-full" style="height:33vh">
        <Bar v-if="execData" :data="execData" :options="execOptions" class="w-full" />
        <LoadingState v-else-if="loadingMetrics" class="absolute inset-0 flex items-center justify-center italic"></LoadingState>
        <div v-else class="absolute inset-0 flex items-center justify-center text-red-600">{{ apiError }}</div>
      </div>
    </section>

    <!-- Cloud Run Job -->
    <section>
      <h2 class="text-lg mb-2">Availability PAC</h2>
      <div class="relative w-full" style="height:33vh">
        <Line v-if="crJobData" :data="crJobData" :options="crJobOptions" class="w-full" />
        <LoadingState v-else-if="loadingMetrics" class="absolute inset-0 flex items-center justify-center italic"></LoadingState>
        <div v-else class="absolute inset-0 flex items-center justify-center text-red-600">{{ apiError }}</div>
      </div>
    </section>

    <!-- Jira Incident SLAs -->
    <section>
      <div class="flex items-end gap-3">
        <h2 class="text-lg mb-2">Incident SLAs — Count</h2>
        <span v-if="incPeriodLabel" class="text-sm text-gray-500">({{ incPeriodLabel }})</span>
      </div>

      <div v-if="loadingIncidents" class="italic">Loading incidents…</div>
      <div v-else-if="apiError" class="text-red-600">{{ apiError }}</div>

      <table v-else class="min-w-full border text-sm">
        <thead class="bg-gray-50">
          <tr>
            <th class="p-2 border text-left">Metric</th>
            <th class="p-2 border text-left">Target</th>
            <th class="p-2 border text-left">Actual</th>
            <th class="p-2 border text-left">SLA Status</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in incRows" :key="row.metric">
            <td class="p-2 border">{{ row.metric }}</td>
            <td class="p-2 border">{{ row.target }}</td>
            <td class="p-2 border">{{ row.actual }} incident<span v-if="row.actual !== 1">s</span></td>
            <td class="p-2 border">
              <span :class="row.status === 'Met' ? 'text-green-600' : 'text-red-600'">{{ row.status }}</span>
            </td>
          </tr>
        </tbody>
      </table>
    </section>

    <!-- Release notes -->
    <section>
      <h2 class="text-lg mb-2">Release notes</h2>
      <p class="text-lg text-gray-700">
        See the latest notes right
        <a
          href="/docs/release_notes/"
          target="_blank"
          class="text-blue-600 underline hover:text-blue-800"
        >
          here
        </a>
      </p>
    </section>

  </div>
</template>


<script setup>
import { ref, onMounted, computed } from 'vue'
import confetti from 'canvas-confetti'
import { Line, Bar } from 'vue-chartjs'
import {
  Chart as ChartJS,
  LineController, LineElement, PointElement,
  BarElement, BarController,
  LinearScale, CategoryScale,
  Title, Tooltip, Legend,
} from 'chart.js'

import { parseDateTime } from '@/src/utils/utils'

// Chart.js registration
ChartJS.register(
  LineController, LineElement, PointElement,
  BarElement, BarController,
  LinearScale, CategoryScale,
  Title, Tooltip, Legend
)

/* ====== API and runtime config ====== */
import { getIncidentCount, getMetrics, getSlaConfig } from '@/src/api/monitoring'
import LoadingState from '@/src/shared-components/LoadingState.vue'
const cfg = ref(null)

// The report loads its panels as one sequence, so it tracks that sequence rather than each
// request: a single flag and a single error is exactly what the template renders.
const loading = ref(true)
const apiError = ref(null)
const loadingMetrics = loading
const loadingIncidents = loading

/* ====== State ====== */
const uptimeData  = ref(null)
const latencyData = ref(null)

const sloHealthData = ref(null)
const sloBudgetData = ref(null)

const execData    = ref(null)

const crJobData = ref(null)
const confettiShown = ref(false)

/* Jira incident count state */
const incRows    = ref([])
const incPeriod  = ref({ start: '', end: '', range: '' })
const incPeriodLabel = computed(() => {
  if (!incPeriod.value.start) return ''
  const s = parseDateTime(incPeriod.value.start)
  const e = parseDateTime(incPeriod.value.end)
  return `${incPeriod.value.range} • ${s} — ${e}`
})

/* ====== Chart options ====== */
const baseX = { display: true, grid: { display: false }, ticks: { autoSkip: true, maxTicksLimit: 12 } }

const uptimeOptions = { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: true, position: 'top' } }, scales: { x: baseX, y: { beginAtZero: true, min: 0, max: 1, ticks: { stepSize: 0.2 } } } }
const latencyOptions = { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: true, position: 'top' } }, scales: { x: baseX, y: { beginAtZero: true } } }
const sloHealthOptions = { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: true, position: 'top' } }, scales: { x: baseX, y: { beginAtZero: true, min: 0, max: 1, ticks: { stepSize: 0.1 } } } }
const sloBudgetOptions = { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: true, position: 'top' } }, scales: { x: baseX, y: { beginAtZero: false } } }
const execOptions = { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: true, position: 'top' } }, scales: { x: baseX, y: { beginAtZero: true, title: { display: true, text: 'minutes' } } } }
const crJobOptions = { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: true, position: 'top' } }, scales: { x: baseX, y: { beginAtZero: true, min: 0, max: 1, ticks: { stepSize: 0.2 } } } }

/* ====== SLA Banner ====== */
const slaBanner = ref(null)
const slaReason = ref('')
const slaReady  = ref(false)

/* Month targets */
const SLA = {
  AVAIL_BH: 0.99,          // Availability (Business)
  LATENCY_P95_MS: 5000,    // Page load time p95 < 5s
  EXEC_P95_MIN: 1,         // Calculation p95 < 1 min
  ERROR_BUDGET_MIN: 0.90,  // Error-budget > 90%
  JOB_SUCCESS_MEAN: 0.95,  // PAC job success > 95%
  P1_MAX: 1,               // P1 <= 1 a month
}

/* helpers */
const toNums = (arr) => (arr ?? []).map(Number).filter(n => Number.isFinite(n))
const mean   = (arr) => {
  const v = toNums(arr)
  return v.length ? v.reduce((a,b)=>a+b,0) / v.length : NaN
}
const percentile = (arr, q) => {
  const v = toNums(arr).slice().sort((a,b)=>a-b)
  if (!v.length) return NaN
  const idx = (v.length - 1) * q
  const lo = Math.floor(idx), hi = Math.ceil(idx)
  return lo === hi ? v[lo] : v[lo] + (v[hi] - v[lo]) * (idx - lo)
}

/* SLA banner check */
function evaluateSLA () {
  let broken = []

  // Availability (media)
  const uptimeMean = mean(uptimeData.value?.datasets?.[0]?.data)
  if (!Number.isNaN(uptimeMean) && uptimeMean < SLA.AVAIL_BH) broken.push('Availability')

  // Page load time p95 (ms)
  const latencyP95 = percentile(latencyData.value?.datasets?.[0]?.data, 0.95)
  if (!Number.isNaN(latencyP95) && latencyP95 > SLA.LATENCY_P95_MS) broken.push('Page load time')

  // SLO Health (media)
  const sloHealthMean = mean(sloHealthData.value?.datasets?.[0]?.data)
  if (!Number.isNaN(sloHealthMean) && sloHealthMean < SLA.AVAIL_BH) broken.push('Celonis availability')

  // Error budget
  const ebVals = toNums(sloBudgetData.value?.datasets?.[0]?.data)
  const ebLast = ebVals.length ? ebVals[ebVals.length-1] : NaN
  if (!Number.isNaN(ebLast) && ebLast < SLA.ERROR_BUDGET_MIN) broken.push('Error budget')

  // PAC job success
  const jobMean = mean(crJobData.value?.datasets?.[0]?.data)
  if (!Number.isNaN(jobMean) && jobMean < SLA.JOB_SUCCESS_MEAN) broken.push('PAC job success')

  // Calculation/Execution time:
  const execValsMin = toNums(execData.value?.datasets?.[0]?.data).filter(v => v > 0)
  const execP95 = percentile(execValsMin, 0.95)
  if (!Number.isNaN(execP95) && execP95 > SLA.EXEC_P95_MIN) broken.push('Calculation time p95')

  // Incidents P1
  const p1Row = (incRows.value || []).find(r => /Critical \(P1\)/i.test(r.metric))
  const p1Actual = typeof p1Row?.actual === 'number' ? p1Row.actual : NaN
  if (!Number.isNaN(p1Actual) && p1Actual > SLA.P1_MAX) broken.push('P1 incidents')

  slaBanner.value = broken.length ? 'BREACHED' : 'MET'
  slaReason.value = broken.length ? broken.join(', ') : 'All targets met'
  slaReady.value = true
  confettiShown.value = false
}
  function handleConfetti () {
    if (slaBanner.value === 'MET' && !confettiShown.value) {
      confetti({
        particleCount: 150,
        spread: 90,
        origin: { y: 0.6 }
      })
      confettiShown.value = true
    }
  }

const mountError = ref(false)

/* ====== Mounted all the metrics this month ====== */
onMounted(async () => {
  try {
    // Load runtime config from backend
    cfg.value = await getSlaConfig()
    // Uptime + Latency (month, every hour)
    const both = await getMetrics({
      type: 'both',
      range: 'month',
      align: cfg.value.align.bi_hourly,
      checker_location: cfg.value.checker_location,
    })

    uptimeData.value = {
      labels: both.uptime.map(p => parseDate(p.end_time)),
      datasets: [{
        label: 'Uptime Check',
        data: both.uptime.map(p => p.value),
        tension: 0.2, borderColor: '#42b983', backgroundColor: '#a2e3c8',
        pointBackgroundColor: '#4b5563', fill: false,
      }],
    }

    latencyData.value = {
      labels: both.latency.map(p => parseDate(p.end_time)),
      datasets: [{
        label: 'Load-time (ms)',
        data: both.latency.map(p => p.value),
        tension: 0.2, borderColor: '#64a0dd', backgroundColor: '#7fbbef',
        pointBackgroundColor: '#4b5563', fill: false,
      }],
    }

    // SLO (month, every hour)
    const [health, budget] = await Promise.all([
      getMetrics({ type: 'slo_health', range: 'month', align: cfg.value.align.hourly, slo_name: cfg.value.slo_name }),
      getMetrics({ type: 'slo_budget', range: 'month', align: cfg.value.align.hourly, slo_name: cfg.value.slo_name }),
    ])

    const healthArr = health.slo ?? []
    const budgetArr = budget.slo ?? []

    sloHealthData.value = {
      labels: healthArr.map(p => parseDate(p.end_time)),
      datasets: [{
        label: 'Availability Celonis',
        data: healthArr.map(p => p.value),
        tension: 0.2,
        borderColor: '#42b983',
        backgroundColor: '#a2e3c8',
        pointBackgroundColor: '#4b5563',
        fill: false,
      }],
    }

    sloBudgetData.value = {
      labels: budgetArr.map(p => parseDate(p.end_time)),
      datasets: [{
        label: 'Error-budget Remaining',
        data: budgetArr.map(p => p.value),
        tension: 0.2,
        borderColor: '#f59e0b',
        backgroundColor: '#fde68a',
        pointBackgroundColor: '#4b5563',
        fill: false,
      }],
    }

    // Cloud Run Job success-rate (month, every 6 hours )
    const cr = await getMetrics({ type: 'cr_job_success', job: cfg.value.job_name, range: 'month', align: cfg.value.align.six_hours, location: cfg.value.job_location })
    const crPoints = cr.cr_job_success || []
    crJobData.value = {
      labels: crPoints.map(p => parseDate(p.end_time)),
      datasets: [{
        label: 'Job success rate',
        data: crPoints.map(p => p.value),
        tension: 0.2, borderColor: '#10b981', backgroundColor: '#bbf7d0',
        pointBackgroundColor: '#4b5563', fill: false,
      }],
    }

    // Execution time from logs (Distribution → p50) month, every 6 hour
    const exec = await getMetrics({
      type: 'exec_time',
      metric_id: cfg.value.log_metric_id,
      range: 'month',
      align: cfg.value.align.six_hours,
      agg: 'p50',
    })
    const execPoints = exec.exec_time || []
    execData.value = {
      labels: execPoints.map(p => parseDate(p.end_time)),
      datasets: [{
        label: 'Calculation/Execution Time (minutes)',
        data: execPoints.map(p => (p.value ?? 0) / 60),
        borderWidth: 1,
        backgroundColor: '#7fbbef',
        borderColor: '#5495d8',
      }],
    }

    // --- Jira Incident SLAs — Count (month) --- //
    const inc = await getIncidentCount({ range: 'month', jql: cfg.value.default_jql })
    incRows.value = inc.rows || []
    incPeriod.value = inc.period || {}

    evaluateSLA()
  } catch (e) {
    console.log(e)
    apiError.value = e?.message || 'Unable to load SLA report data'
    mountError.value = apiError.value
    return
  } finally {
    loading.value = false
  }
})
</script>